import { create } from "zustand";

/**
 * Per-device sidebar layout preferences — localStorage only, never synced.
 *
 * Two things live here, under two keys, because they are written by two
 * different surfaces:
 *  - `prefs` (`sidebar_prefs`): what the gear next to "Main Menu" edits
 *    (sidebar-settings-dialog.tsx) — the Main Menu row order, and per
 *    collapsible group whether it starts open and how many sub-items it
 *    lists inline before the "+N more" popover.
 *  - `openGroups` (`sidebar_open_groups`): the last chevron toggle per group,
 *    so the sidebar reopens exactly as it was left. Saving the dialog clears
 *    it, which makes the new "open by default" take effect right away.
 *
 * Consumers: app-sidebar.tsx (order + the gear) and the three nav groups via
 * `useSidebarGroup`. The collapsed-sidebar hover cards deliberately ignore
 * `itemsShown` — they are the "see all" surface.
 */

export const SIDEBAR_MENU_KEYS = [
  "search",
  "campaigns",
  "amy",
  "crm",
  "tasks",
  "settings",
] as const;
export type SidebarMenuKey = (typeof SIDEBAR_MENU_KEYS)[number];

export const SIDEBAR_GROUP_KEYS = ["campaigns", "amy", "settings"] as const;
export type SidebarGroupKey = (typeof SIDEBAR_GROUP_KEYS)[number];

export interface SidebarGroupPrefs {
  /** Start expanded (only meaningful while `itemsShown > 0`). */
  defaultOpen: boolean;
  /** Sub-items rendered inline before "+N more". 0 = the group never expands. */
  itemsShown: number;
}

export interface SidebarPrefs {
  order: SidebarMenuKey[];
  groups: Record<SidebarGroupKey, SidebarGroupPrefs>;
}

export const MAX_ITEMS_SHOWN = 20;

export const DEFAULT_SIDEBAR_PREFS: SidebarPrefs = {
  order: [...SIDEBAR_MENU_KEYS],
  groups: {
    campaigns: { defaultOpen: true, itemsShown: 5 },
    amy: { defaultOpen: false, itemsShown: 5 },
    // Settings has a short fixed category list; the max shows all of them.
    settings: { defaultOpen: false, itemsShown: MAX_ITEMS_SHOWN },
  },
};

const PREFS_KEY = "sidebar_prefs";
const OPEN_GROUPS_KEY = "sidebar_open_groups";

type OpenGroups = Partial<Record<SidebarGroupKey, boolean>>;

const isMenuKey = (v: unknown): v is SidebarMenuKey =>
  typeof v === "string" && (SIDEBAR_MENU_KEYS as readonly string[]).includes(v);

export const isGroupKey = (v: unknown): v is SidebarGroupKey =>
  typeof v === "string" && (SIDEBAR_GROUP_KEYS as readonly string[]).includes(v);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

function clampCount(v: unknown, fallback: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return fallback;
  return Math.min(MAX_ITEMS_SHOWN, Math.max(0, Math.round(v)));
}

/**
 * Tolerant parse of whatever is in storage: unknown/duplicate menu keys are
 * dropped, keys added since the value was written are appended in default
 * order, counts are clamped, booleans coerced. Never throws.
 */
export function sanitizePrefs(raw: unknown): SidebarPrefs {
  const src = isRecord(raw) ? raw : {};

  const seen = new Set<SidebarMenuKey>();
  const order: SidebarMenuKey[] = [];
  if (Array.isArray(src.order)) {
    for (const key of src.order) {
      if (isMenuKey(key) && !seen.has(key)) {
        seen.add(key);
        order.push(key);
      }
    }
  }
  for (const key of SIDEBAR_MENU_KEYS) {
    if (!seen.has(key)) order.push(key);
  }

  const rawGroups = isRecord(src.groups) ? src.groups : {};
  const groups = {} as SidebarPrefs["groups"];
  for (const key of SIDEBAR_GROUP_KEYS) {
    const def = DEFAULT_SIDEBAR_PREFS.groups[key];
    const g = isRecord(rawGroups[key]) ? rawGroups[key] : {};
    groups[key] = {
      defaultOpen:
        typeof g.defaultOpen === "boolean" ? g.defaultOpen : def.defaultOpen,
      itemsShown: clampCount(g.itemsShown, def.itemsShown),
    };
  }

  return { order, groups };
}

function sanitizeOpenGroups(raw: unknown): OpenGroups {
  if (!isRecord(raw)) return {};
  const out: OpenGroups = {};
  for (const [key, value] of Object.entries(raw)) {
    if (isGroupKey(key) && typeof value === "boolean") out[key] = value;
  }
  return out;
}

function readJson(key: string): unknown {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full / blocked — the in-memory state still applies this session.
  }
}

export const prefsAreEqual = (a: SidebarPrefs, b: SidebarPrefs) =>
  JSON.stringify(a) === JSON.stringify(b);

interface SidebarPrefsStore {
  prefs: SidebarPrefs;
  openGroups: OpenGroups;
  /** Chevron toggle — remembered across reloads. */
  setGroupOpen: (key: SidebarGroupKey, open: boolean) => void;
  /** Dialog Save — persists prefs and forgets chevron toggles so defaults apply. */
  savePrefs: (prefs: SidebarPrefs) => void;
}

export const useSidebarPrefsStore = create<SidebarPrefsStore>((set) => ({
  prefs: sanitizePrefs(readJson(PREFS_KEY)),
  openGroups: sanitizeOpenGroups(readJson(OPEN_GROUPS_KEY)),

  setGroupOpen: (key, open) =>
    set((state) => {
      const openGroups = { ...state.openGroups, [key]: open };
      writeJson(OPEN_GROUPS_KEY, openGroups);
      return { openGroups };
    }),

  savePrefs: (next) => {
    const prefs = sanitizePrefs(next);
    writeJson(PREFS_KEY, prefs);
    writeJson(OPEN_GROUPS_KEY, {});
    set({ prefs, openGroups: {} });
  },
}));

/**
 * What a collapsible nav group needs: its resolved open state (last chevron
 * toggle, else the configured default — forced closed when it shows 0 items),
 * the setter for its chevron, and how many sub-items to render inline.
 */
export function useSidebarGroup(key: SidebarGroupKey) {
  const itemsShown = useSidebarPrefsStore((s) => s.prefs.groups[key].itemsShown);
  const open = useSidebarPrefsStore(
    (s) => itemsShown > 0 && (s.openGroups[key] ?? s.prefs.groups[key].defaultOpen)
  );
  const setGroupOpen = useSidebarPrefsStore((s) => s.setGroupOpen);
  return {
    open,
    setOpen: (value: boolean) => setGroupOpen(key, value),
    itemsShown,
  };
}
